/**
 * @module features/profile/components/PasskeyDeviceList
 * @description Manages registered FIDO2 / WebAuthn biometric and security key credentials,
 * enforcing the 5-device quota and safeguard against last-credential removal.
 */

import * as React from "react";
import {
  Fingerprint,
  Plus,
  Trash2,
  Laptop,
  AlertCircle,
  Loader2,
  Calendar,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  usePasskeyRegisterOptions,
  useVerifyPasskeyRegister,
  useDeletePasskey,
} from "../api/profile-api";
import { registerPasskeyDevice, browserSupportsWebAuthn } from "@/lib/webauthn";
import { PasskeyDeviceDto } from "@/types/profile";
import { AppError } from "@/lib/api-client";

interface PasskeyDeviceListProps {
  passkeys: PasskeyDeviceDto[];
  maxPasskeys?: number;
}

export function PasskeyDeviceList({
  passkeys,
  maxPasskeys = 5,
}: PasskeyDeviceListProps) {
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [deviceLabel, setDeviceLabel] = React.useState("");
  const [addError, setAddError] = React.useState<string | null>(null);
  const [isRegistering, setIsRegistering] = React.useState(false);

  const [deleteTarget, setDeleteTarget] = React.useState<PasskeyDeviceDto | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const registerOptionsMutation = usePasskeyRegisterOptions();
  const verifyRegisterMutation = useVerifyPasskeyRegister();
  const deleteMutation = useDeletePasskey();

  const isLimitReached = passkeys.length >= maxPasskeys;
  const webAuthnSupported = browserSupportsWebAuthn();

  const handleStartRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    setIsRegistering(true);

    try {
      const options = await registerOptionsMutation.mutateAsync();
      const credential = await registerPasskeyDevice(options);

      await verifyRegisterMutation.mutateAsync({
        credential,
        device_label: deviceLabel.trim() || "Biometric Authenticator",
      });

      setIsAddModalOpen(false);
      setDeviceLabel("");
    } catch (err: any) {
      if (err instanceof AppError) {
        setAddError(err.errorDetails);
      } else if (err.name === "NotAllowedError") {
        setAddError("Biometric or security key prompt was cancelled.");
      } else {
        setAddError(err.message || "Failed to register passkey device.");
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleDeletePasskey = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);

    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err: any) {
      if (err instanceof AppError) {
        setDeleteError(err.errorDetails);
      } else {
        setDeleteError("Failed to remove passkey device.");
      }
    }
  };

  return (
    <Card className="border-border/80 dark:border-zinc-800 bg-card dark:bg-zinc-950 shadow-xs rounded-2xl">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs">
            <Fingerprint className="h-4.5 w-4.5 shrink-0 stroke-[2.2]" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-sm sm:text-base font-semibold text-foreground">
                Passkeys & Security Keys
              </CardTitle>
              <span className="font-mono text-[11px] font-semibold px-2 py-0.5 rounded-full bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shrink-0 shadow-xs">
                {passkeys.length} / {maxPasskeys}
              </span>
            </div>
          </div>
        </div>

        <Button
          size="sm"
          disabled={isLimitReached || !webAuthnSupported}
          onClick={() => {
            setAddError(null);
            setDeviceLabel("");
            setIsAddModalOpen(true);
          }}
          className="h-9 px-3.5 text-xs shrink-0 whitespace-nowrap bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5 shrink-0" />
          <span>Register Passkey</span>
        </Button>
      </CardHeader>

      <CardContent>
        {!webAuthnSupported && (
          <div className="mb-4 flex items-center gap-2 p-3 text-xs rounded-lg bg-warning-soft text-warning border border-warning/20">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>WebAuthn passkeys are not supported on this browser or platform.</span>
          </div>
        )}

        {passkeys.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center">
            <Fingerprint className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-2 text-sm font-medium text-foreground">
              No passkeys registered yet
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Add a passkey to enable fast, passwordless biometric login.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
            {passkeys.map((device) => (
              <div
                key={device.id}
                className="flex items-center justify-between p-3.5 sm:p-4 bg-card hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs">
                    <Laptop className="h-4 w-4 shrink-0" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {device.device_label}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono mt-0.5">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 shrink-0" />
                        Added {new Date(device.created_at).toLocaleDateString()}
                      </span>
                      {device.last_used_at && (
                        <span className="hidden sm:inline">
                          &bull; Last used {new Date(device.last_used_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteTarget(device);
                  }}
                  className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                  title="Remove Passkey"
                >
                  <Trash2 className="h-4 w-4 shrink-0" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* ------------------------------------------------------------- */}
      {/* Add Passkey Modal / Sheet */}
      {/* ------------------------------------------------------------- */}
      <Dialog
        open={isAddModalOpen}
        onOpenChange={(isOpen) => !isOpen && !isRegistering && setIsAddModalOpen(false)}
      >
        <DialogContent className="sm:max-w-md" sheetOnMobile>
          <DialogHeader>
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-xs">
                <Fingerprint className="h-4.5 w-4.5 shrink-0" />
              </div>
              <DialogTitle className="text-base font-semibold">Register New Passkey</DialogTitle>
            </div>
            <DialogDescription>
              Assign a recognizable device label before your browser prompts for biometrics.
            </DialogDescription>
          </DialogHeader>

          {addError && (
            <div className="mt-2 flex items-center gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{addError}</span>
            </div>
          )}

          <form onSubmit={handleStartRegistration} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <label
                htmlFor="device_label"
                className="text-xs font-medium text-foreground"
              >
                Device Label
              </label>
              <Input
                id="device_label"
                placeholder="e.g. Work MacBook Touch ID or YubiKey"
                value={deviceLabel}
                onChange={(e) => setDeviceLabel(e.target.value)}
                className="h-10 text-sm"
              />
            </div>

            <DialogFooter className="pt-2 flex flex-col-reverse sm:flex-row gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                disabled={isRegistering}
                onClick={() => setIsAddModalOpen(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isRegistering}
                className="w-full sm:w-auto min-w-[120px] bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
              >
                {isRegistering ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Touch Sensor...
                  </>
                ) : (
                  "Continue to Sensor"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------- */}
      {/* Delete Passkey Confirmation Modal / Sheet */}
      {/* ------------------------------------------------------------- */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(isOpen) => !isOpen && !deleteMutation.isPending && setDeleteTarget(null)}
      >
        <DialogContent className="sm:max-w-md" sheetOnMobile>
          <DialogHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <DialogTitle>Remove Passkey Device</DialogTitle>
            </div>
            <DialogDescription className="pt-2 text-sm">
              Are you sure you want to revoke{" "}
              <strong className="text-foreground">{deleteTarget?.device_label}</strong>?
              You will no longer be able to use this biometric or security key to sign in.
            </DialogDescription>
          </DialogHeader>

          {deleteError && (
            <div className="p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
              {deleteError}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0 flex flex-col-reverse sm:flex-row">
            <Button
              variant="outline"
              disabled={deleteMutation.isPending}
              onClick={() => setDeleteTarget(null)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={handleDeletePasskey}
              className="w-full sm:w-auto"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                "Revoke Passkey"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
